import {
  useEffect, useRef, useState,
} from 'react';
import {
  Row, Col, Select, Modal,
} from 'antd';
import {
  any, array, string, object, bool,
} from 'prop-types';

import Layout from 'components/layout';
import HeadSEO from 'components/base/headSEO';
import { MyOrderModal } from 'components/myOrderModal';
import { initCMS } from 'utils/cms';
import {
  getFAQs,
  getPaymentMethods,
  getShippingMethods,
} from 'actions/general';
import { getFaqsSizeGuide } from 'actions/faqs';
import { useI18n } from 'context/i18n';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { CloseModalSVG } from 'components/iconsSVG/close';
import { getMarket, getIsMobileSSR } from 'utils/helpers';
import dynamic from 'next/dynamic';
import { MyAccountModal } from 'components/myAccount';
import { MARKETS_WITH_COUNTRIES_SELECTOR } from 'utils/constants/i18n';
import { IS_CAMPER } from 'utils/constants/system';

import styles from 'styles/CustomerService.module.css';

const baseUrl = process.env.INTEGRA_DOMAIN;

const ReturnCostsFaq = dynamic(() => import('components/returnCostsFaq').then((mod) => mod.ReturnCostsFaq));

const CONFIANZA_FAQ = 18;
const PAYMENT_FAQ = 21;
const SIZES_FAQ = 31;
const SHIPPING_FAQ = 46;
const CANCEL_ORDER_FAQ = 52;
const RETURN_ORDER_FAQ = 54;
const CHANGE_SIZE_FAQ = 56;
const ACCESS_MY_ACCOUNT_FAQ = 75;
const TROUBLE_SIGNING_MY_ACCOUNT_FAQ = 76;
const RETURN_CONDITIONS_FAQ = 514;

const PAYMENT_IMG_SRC = {
  AFTY: {
    src: '/assets-new/img/payments/afterpay.png',
    width: 30,
  },
  ALIP: {
    src: '/assets-new/img/payments/ALIP.png',
    width: 30,
  },
  BIZY: {
    src: '/assets-new/img/payments/BIZY.svg',
    width: 50,
  },
  CTR: {
    src: '/assets-new/img/payments/CTR.png',
    width: 30,
  },
  DOTY: {
    src: '/assets-new/img/payments/DOTY.png',
    width: 30,
  },
  PRED: {
    src: '/assets-new/img/payments/PRED.png',
    width: 30,
  },
  IDEB: {
    src: '/assets-new/img/payments/ideal.jpg',
    width: 30,
  },
  IYZP: {
    src: '/assets-new/img/payments/TARJ.gif',
    width: 30,
  },
  KLRY: {
    src: '/assets-new/img/payments/klarna.svg',
    width: 30,
  },
  LGUP: {
    src: '/assets-new/img/payments/LGUP.png',
    width: 30,
  },
  MERP: {
    src: '/assets-new/img/payments/MERP.svg',
    width: 50,
  },
  MRCB: {
    src: '/assets-new/img/payments/MRCB.png',
    width: 30,
  },
  NAVR: {
    src: '/assets-new/img/payments/NAVR.png',
    width: 30,
  },
  PPLB: {
    src: '/assets-new/img/payments/PPLB.png',
    width: 30,
  },
  PPLY: {
    src: '/assets-new/img/payments/PPLB.png',
    width: 30,
  },
  SOFB: {
    src: '/assets-new/img/payments/sofort.png',
    width: 30,
  },
  SOFY: {
    src: '/assets-new/img/payments/sofort.png',
    width: 30,
  },
  TARG: {
    src: '/assets-new/img/payments/TARJ.gif',
    width: 30,
  },
  TARJ: {
    src: '/assets-new/img/payments/TARJ.gif',
    width: 30,
  },
  TOSS: {
    src: '/assets-new/img/payments/TOSS.svg',
    width: 50,
  },
  TRAY: {
    src: '/assets-new/img/payments/TRAY.png',
    width: 30,
  },
  TRNB: {
    src: '/assets-new/img/payments/TRNB.svg',
    width: 50,
  },
  VISC: {
    src: '/assets-new/img/payments/TARJ.gif',
    width: 30,
  },
  YOOM: {
    src: '/assets-new/img/payments/TARJ.gif',
    width: 30,
  },
};

function AtClienteFaq({
  footerProps, tnmProps, ribbon, categories, currentFaq, isGroup, currentFaqs, currentCategory,
  paymentMethods, shippingMethods, sizeGuides, urlCanonical,
}) {
  const {
    t, countries, profileData: {
      homePickUp, homePickUpPrice, global_e, storeId, salesOrg,
    }, locale,
  } = useI18n();
  const countryId = getMarket(locale);

  const countryRef = useRef();
  const [ breadcrumbFragments, setBreadcrumbFragments ] = useState([]);
  const [ selectedCountry, setSelectedCountry ] = useState(countryId);
  const [ filteredPaymentMethods, setFilteredPaymentMethods ] = useState(() => (global_e ? [] : JSON.parse(JSON.stringify(paymentMethods))));
  const [ filteredShippingMethods, setFilteredShippingMethods ] = useState(JSON.parse(JSON.stringify(shippingMethods)));
  const [ modalFaqsOpen, setModalFaqsOpen ] = useState(false);
  const [ selectedFAQ, setSelectedFAQ ] = useState(null);
  const [ showingMyAccountModal, setShowingMyAccountModal ] = useState(false);
  const [ showingMyOrderModal, setShowingMyOrderModal ] = useState(false);
  const [ initialRetrievePassword, setInitialRetrievePassword ] = useState(false);

  const featuredFaqs = categories.map((category) => category.faqs.filter((faq) => faq.featured)).flat();
  featuredFaqs.sort((a, b) => {
    if (a.featuredSort > b.featuredSort) {
      return 1;
    }
    return b.featuredSort > a.featuredSort ? -1 : 0;
  });

  // Breadcrumbs
  useEffect(() => {
    const breadcrumbs = [];
    breadcrumbs.push({
      title: t('generico', 'breadcrumb.home', '**Home'),
      href: `/${locale}/`,
    });
    breadcrumbs.push({
      title: t('faq', 'customer.service', 'Customer Service'),
      href: `/${locale}/customer_service`,
    });
    if (isGroup && currentCategory && 'id' in currentCategory) {
      breadcrumbs.push({
        title: currentCategory.text,
      });
    } else {
      breadcrumbs.push({
        title: currentFaq.question,
      });
    }
    setBreadcrumbFragments(breadcrumbs);
  }, []);

  const handleChangeCountry = async (country, isShipping = false) => {
    setSelectedCountry(country);
    try {
      if (!isShipping) {
        const paymentsRs = await getPaymentMethods({ lang: locale, countryId: country });
        const newPaymentMethods = paymentsRs.paymentMethods || [];
        setFilteredPaymentMethods(newPaymentMethods);
      } else {
        const shipmentsRs = await getShippingMethods({ lang: locale, countryId: country });
        const newShipmentMethods = shipmentsRs.shippingMethods || [];
        setFilteredShippingMethods(newShipmentMethods);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Return Order
  let returnOrderAnchor = '';
  if (homePickUp) {
    if (homePickUpPrice === 0) {
      returnOrderAnchor = `devoluciones.devolver.pedido.homepickup.free.${storeId}`;
    } else {
      returnOrderAnchor = `devoluciones.devolver.pedido.homepickup.${storeId}`;
    }
  } else {
    returnOrderAnchor = `devoluciones.devolver.pedido.normal.${storeId}`;
    if (countryId === 'CA' || countryId === 'US') {
      returnOrderAnchor = `${returnOrderAnchor}.${countryId}`;
    }
  }

  // Replace saltos de línea
  const returnOrderModalTexts = t('faq', returnOrderAnchor).replace(/(?:\r\n|\r|\n)/g, '<br />')
    .split('<TAG layout mispedidos>');

  // Change Size
  let changeSizeAnchor = '';
  if (homePickUp) {
    if (homePickUpPrice === 0) {
      changeSizeAnchor = `devoluciones.cambiar.talla.homepickup.free.${storeId}`;
    } else {
      changeSizeAnchor = `devoluciones.cambiar.talla.homepickup.${storeId}`;
    }
  } else {
    changeSizeAnchor = `devoluciones.cambiar.talla.normal.${storeId}`;
  }

  // Replace saltos de línea
  const changeSizeModalTexts = t('faq', changeSizeAnchor).replace(/(?:\r\n|\r|\n)/g, '<br />')
    .split('<TAG layout mispedidos>');

  const modalMapper = {
    returnOrder: <div>
      <h3 className={styles.modalQuestion}>{t('faq', `devoluciones.devolver.pedido.${storeId}`)}</h3>
      <div className={styles.modalAnswer}>
        <span dangerouslySetInnerHTML={{ __html: returnOrderModalTexts[0] }} />
        <a href="#" onClick={() => { setModalFaqsOpen(false); setShowingMyOrderModal(true); } }>
          {t('menu.superior', 'mis.pedidos')}
        </a>
        <span dangerouslySetInnerHTML={{ __html: returnOrderModalTexts[1] }} />
      </div>
    </div>,
    changeSize: <div>
    <h3 className={styles.modalQuestion}>{t('faq', `devoluciones.cambiar.talla.${storeId}`)}</h3>
    <div className={styles.modalAnswer}>
      <span dangerouslySetInnerHTML={{ __html: changeSizeModalTexts[0] }} />
      <a href="#" onClick={() => { setModalFaqsOpen(false); setShowingMyOrderModal(true); } }>
        {t('menu.superior', 'mis.pedidos')}
      </a>
      <span dangerouslySetInnerHTML={{ __html: changeSizeModalTexts[1] }} />
    </div>
  </div>,
  };

  const openReturnOrderModal = (e) => {
    e.preventDefault();
    setSelectedFAQ('returnOrder');
    setModalFaqsOpen(true);
  };

  const openChangeSizeModal = (e) => {
    e.preventDefault();
    setSelectedFAQ('changeSize');
    setModalFaqsOpen(true);
  };

  const renderAnswer = (answer, faqId) => {
    const isPaymentFaq = faqId === PAYMENT_FAQ;
    const isShippingFaq = faqId === SHIPPING_FAQ;
    const isConfianzaFaq = faqId === CONFIANZA_FAQ;
    const isSizesFaq = faqId === SIZES_FAQ;
    const isCancelOrderFaq = faqId === CANCEL_ORDER_FAQ;
    const isReturnOrderFaq = faqId === RETURN_ORDER_FAQ;
    const isChangeSizeFaq = faqId === CHANGE_SIZE_FAQ;
    const isAccessMyAccountFaq = faqId === ACCESS_MY_ACCOUNT_FAQ;
    const isTroubleSigningMyAccountFaq = faqId === TROUBLE_SIGNING_MY_ACCOUNT_FAQ;
    const isReturnConditionsFaq = faqId === RETURN_CONDITIONS_FAQ;

    if (!isPaymentFaq && !isShippingFaq && !isSizesFaq && !isCancelOrderFaq && !isReturnOrderFaq &&
        !isChangeSizeFaq && !isAccessMyAccountFaq && !isTroubleSigningMyAccountFaq && !isReturnConditionsFaq) {
      answer = answer.replace(
        /<a onclick="cambiaQ\('(\d+)'\);return false;"\s*>/g,
        `<a href="/${locale}/at_cliente_faq?faq=$1">`,
      );

      if (IS_CAMPER && isConfianzaFaq) {
        let sellos = `<div>
          <a href="https://www.confianzaonline.es/empresas/camper.htm" target="_blank" rel="noreferrer">
            <img border="0" alt="Entidad adherida a Confianza Online" src="https://www.confianzaonline.es/selloanimado.gif" />
          </a>
        </div>`;
        if (salesOrg !== 'ES00') {
          sellos = `<div>
            <a href="http://www.verisign.com/ssl-certificate/" target="_blank" rel="noreferrer">
              ABOUT SSL CERTIFICATES
            </a>
          </div>`;
        }

        answer = answer.replace('<SELLOS>', sellos);
      }

      return (
        <div>
          <div className={`${styles.answer} ${styles.answerLast}`} dangerouslySetInnerHTML={{ __html: answer }} />
        </div>
      );
    }

    if (isReturnConditionsFaq) {
      const answerArray = answer.split('<TAG selector devoluciones>');

      return (
        <div>
          <div className={styles.answer} dangerouslySetInnerHTML={{ __html: answerArray[0] }} />
          <ReturnCostsFaq />
          <div className={`${styles.answer} ${styles.answerLast}`} dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
        </div>
      );
    }

    const countrySelector = global_e ? null : (
      <div className={styles.selectGroup}>
        <Select
          ref={countryRef}
          placeholder={t('faqs', 'select.country', 'Select a country')}
          allowClear={false}
          filterOption={false}
          suffixIcon ={<ChevronSVG />}
          onChange={(val) => {
            countryRef.current.blur();
            handleChangeCountry(val, isShippingFaq || isReturnOrderFaq || isChangeSizeFaq);
          }}
          value={selectedCountry}
          className={styles.modalSelect}
        >
          {countries.filter((c) => !c.globale).map((country) => (
            <Select.Option key={country.id} className={styles.selectItem}>
              <p>{country.name}</p>
            </Select.Option>
          ))}
        </Select>
      </div>
    );

    if (isShippingFaq) {
      const answerArray = answer.split('<TAG selector envios>');
      return (
        <>
          <div className={styles.answer} dangerouslySetInnerHTML={{ __html: answerArray[0] }} />
          {countrySelector}
          {!global_e ? <div className={styles.shippingMethods}>
            {filteredShippingMethods.map((sm, idx1) => (
              <div key={idx1}>
                <div className={styles.shippingTitle}>
                  <div className={styles.shippingName}>{sm.name}</div>
                  <div className={styles.shippingPrice}>{sm.priceFormated}</div>
                </div>
                { sm.infoList && sm.infoList.length > 0 &&
                  <div className={styles.shippingInfoList}>
                    {sm.infoList.map((info, idx2) => (
                      <div key={idx2} dangerouslySetInnerHTML={{ __html: `- ${info}` }} />
                    ))}
                  </div>
                }
              </div>
            ))}
          </div> : null}
          <div className={`${styles.answer} ${styles.answerLast}`} dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
        </>
      );
    }

    if (isSizesFaq) {
      const answerArray = answer.split('<TAG tallas web>');
      return (
        <>
          <div className={styles.answer} dangerouslySetInnerHTML={{ __html: answerArray[0] }} />
          <div>
            {sizeGuides.map((sizesGuide) => (
              <div key={sizesGuide.targetAnchor}>
                {t('faq', sizesGuide.targetAnchor)}
                <div className={styles.selectGroup}>
                  <Select
                    allowClear={false}
                    filterOption={false}
                    suffixIcon ={<ChevronSVG />}
                    defaultValue={sizesGuide.sizes[0]}
                    className={styles.modalSelect}
                  >
                    {sizesGuide.sizes && sizesGuide.sizes.length > 0 && sizesGuide.sizes.map((size) => (
                      <Select.Option key={size} className={styles.selectItem}>
                        <p>{size.replace(/,5/g, '½')}</p>
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <div className={`${styles.answer} ${styles.answerLast}`} dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
        </>
      );
    }

    if (isCancelOrderFaq) {
      const answerArray = answer.split('<TAG layout mispedidos>');
      return (
        <div className={`${styles.answer} ${styles.answerLast}`}>
          <span dangerouslySetInnerHTML={{ __html: answerArray[0] }} />
          { answerArray.length > 1 && <>
            <a href="#" onClick={() => { setShowingMyOrderModal(true); } }>
              {t('menu.superior', 'mis.pedidos')}
            </a>
            <span dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
          </> }
        </div>
      );
    }

    if (isReturnOrderFaq || isChangeSizeFaq) {
      const answerArray = answer.split('<TAG aqui>');
      const listOfReturnMethods = answerArray[0].includes('<TAG list of return methods>');
      if (MARKETS_WITH_COUNTRIES_SELECTOR.includes(countryId) && listOfReturnMethods) {
        const answerArray2 = answerArray[0].split('<TAG list of return methods>');
        return (
          <div className={`${styles.answer} ${styles.answerLast}`}>
            { countryId !== 'XI' ?
              <>
                <span dangerouslySetInnerHTML={{ __html: answerArray2[0] }} />
                <ReturnCostsFaq />
                <span dangerouslySetInnerHTML={{ __html: answerArray2[1] }} />
              </> :
              <span>{t('faq', 'devoluciones.xi')}</span>
            }
            <a href="#" onClick={isReturnOrderFaq ? openReturnOrderModal : openChangeSizeModal}>
              {t('faq', 'aqui')}
            </a>
            <span dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
          </div>
        );
      }
      return (
        <div className={`${styles.answer} ${styles.answerLast}`}>
          { isReturnOrderFaq && countryId !== 'XI' ?
            <span dangerouslySetInnerHTML={{ __html: answerArray[0] }} /> :

            <span>{t('faq', 'devoluciones.xi')}</span>
          }
          <a href="#" onClick={isReturnOrderFaq ? openReturnOrderModal : openChangeSizeModal}>
            {t('faq', 'aqui')}
          </a>
          <span dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
        </div>
      );
    }

    if (isAccessMyAccountFaq) {
      const answerArray = answer.split('<TAG acceso cuenta>');
      return (
        <div className={`${styles.answer} ${styles.answerLast}`}>
          <span dangerouslySetInnerHTML={{ __html: answerArray[0] }} />
          { answerArray.length > 1 && <>
            <a href="#" onClick={() => { setShowingMyAccountModal(true); } }>
              {t('faq', 'aqui')}
            </a>
            <span dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
          </> }
        </div>
      );
    }

    if (isTroubleSigningMyAccountFaq) {
      const answerArray = answer.split('<TAG restablecer>');
      return (
        <div className={`${styles.answer} ${styles.answerLast}`}>
          <span dangerouslySetInnerHTML={{ __html: answerArray[0] }} />
          { answerArray.length > 1 && <>
            <a href="#" onClick={() => { setInitialRetrievePassword(true); setShowingMyAccountModal(true); } }>
              {t('faq', 'restablecer.contrasenya')}
            </a>
            <span dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
          </> }
        </div>
      );
    }
    if (global_e && isPaymentFaq) {
      const answerArray = answer.split('<TAG formas pago>');
      return (
        <>
          <div className={styles.answer} dangerouslySetInnerHTML={{ __html: answerArray[0] }} />
          <div className={styles.paymentMethods}>
            {filteredPaymentMethods.map((paymentMethod) => {
              const pm = PAYMENT_IMG_SRC[paymentMethod.id];
              return <p key={paymentMethod.id}>
                <span>{paymentMethod.name}</span>
                {pm &&
                  <img src={pm.src} width={pm.width} height="fit-content" />
                }
              </p>;
            })}
          </div>
          <div className={`${styles.answer} ${styles.answerLast}`} dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
        </>
      );
    }

    const answerArray = answer.split('<TAG formas pago>');
    return (
      <>
        <div className={styles.answer} dangerouslySetInnerHTML={{ __html: answerArray[0] }} />
        {countrySelector}
        <div className={styles.paymentMethods}>
          {filteredPaymentMethods.map((paymentMethod) => {
            const pm = PAYMENT_IMG_SRC[paymentMethod.id];
            return <p key={paymentMethod.id}>
              <span>{paymentMethod.name}</span>
              {pm &&
                <img src={pm.src} width={pm.width} height="fit-content" />
              }
            </p>;
          })}
        </div>
        <div className={`${styles.answer} ${styles.answerLast}`} dangerouslySetInnerHTML={{ __html: answerArray[1] }} />
      </>
    );
  };

  const onClickFaq = (e) => {
    const el = e.target;
    if (el && el.tagName === 'A' && el.dataset && el.dataset.action === 'levantar-pop-up') {
      window.showOrderPopUp();
    }
  };

  return (
    <Layout
      footerProps={footerProps}
      tnmProps={tnmProps}
      ribbon={ribbon}
      breadcrumbFragments={breadcrumbFragments}
      extratags={urlCanonical ? [ <link key='canonical' rel="canonical" href={urlCanonical} /> ] : []}
    >
      <div className="container">
        <HeadSEO title="Atención al cliente" description="Customer Service" pageName='at_cliente_faq' />
        <main>
          <Row>
            <MyAccountModal
              visible={showingMyAccountModal}
              onClose={() => setShowingMyAccountModal(false)}
              initialRetrievePassword={initialRetrievePassword}
            />
            <MyOrderModal
              locale={locale}
              visible={showingMyOrderModal}
              onClose={() => setShowingMyOrderModal(false)}
            />
            <Col span={24} xxl={{ span: 16, offset: 4 }}>
              {!isGroup ?
                <div className={styles.featuredFaqs} onClick={onClickFaq}>
                  <h1 className={`${styles.question} ${styles.questionTitle}`}>
                    <a href={`/${locale}/customer_service`}>
                      <ChevronSVG />
                    </a>
                    {currentFaq.question}
                  </h1>
                  {renderAnswer(currentFaq.answer, currentFaq.id)}
                </div> :
                currentFaqs.map((faq) => (
                  <div key={faq.id} className={styles.featuredFaqs} onClick={onClickFaq}>
                    <h1 className={`${styles.question} ${styles.questionTitle}`}>
                      <a href={`/${locale}/customer_service`}>
                        <ChevronSVG />
                      </a>
                      {faq.question}
                    </h1>
                    {renderAnswer(faq.answer, faq.id)}
                  </div>
                ))

              }
              {featuredFaqs && featuredFaqs.length &&
                <div className={styles.featuredFaqsMain}>
                  <h1 className={styles.title}>{t('faqs', 'titulo.preguntas.frecuentes', 'Frequent questions')}</h1>
                  <div>
                    {featuredFaqs.map((faq) => <p className={styles.question} key={faq.id}>
                      <a href={`/${locale}/at_cliente_faq?faq=${faq.id}`}>
                        {faq.question}
                      </a>
                    </p>)}
                  </div>
                </div>
              }
            </Col>
          </Row>
          <Row>
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 8, offset: 8 }} className={styles.contactBox}>
              <p>{t('faqs', 'texto.ayuda', "Can't find what you're looking for?")}</p>
              <a href={`/${locale}/contacto`}>
                {t('faqs', 'boton.contacto', 'Contact us')}
              </a>
            </Col>
          </Row>

          <Modal
            title={null}
            visible={modalFaqsOpen}
            destroyOnClose={true}
            footer={null}
            onCancel={() => setModalFaqsOpen(false)}
            width="100%"
            className={styles.modal}
            closeIcon={<CloseModalSVG/>}
          >
            {modalMapper[selectedFAQ] === undefined ? null : modalMapper[selectedFAQ]}
          </Modal>

        </main>
      </div>
    </Layout>
  );
}

AtClienteFaq.propTypes = {
  footerProps: any,
  tnmProps: any,
  ribbon: any,
  categories: array,
  currentFaq: object,
  isGroup: bool,
  currentFaqs: array,
  currentCategory: object,
  countries: array,
  paymentMethods: array,
  shippingMethods: array,
  sizeGuides: array,
  urlCanonical: string,
  isGlobalE: bool,
};

AtClienteFaq.defaultProps = {
  footerProps: null,
  tnmProps: null,
  categories: [],
  currentFaq: {},
  isGroup: false,
  currentFaqs: [],
  currentCategory: {},
  countries: [],
  paymentMethods: [],
  shippingMethods: [],
  sizeGuides: [],
};

AtClienteFaq.i18nPage = 'ClientFAQ';

export async function getServerSideProps(context) {
  const { locale, query } = context;
  const [ {
    footerProps, tnmProps, ribbon,
  }, faqsResponse ] = await Promise.all([
    initCMS({ context, locale }),
    getFAQs({ context, lang: locale }),
  ]);
  // faq = es el parámetro estándar
  // question = es lo mismo que faq
  // tit = es el número de categoría. Si llega este parámetro, mostramos todas las FAQs de la categoría
  // question y tit = en ocasiones llegan desde los mails
  const { faq, question, tit } = query;

  const isGroup = tit !== undefined && tit !== null;
  if (!isGroup) {
    const faqId = faq || question;
    const faqs = faqsResponse.categories.map((category) => category.faqs).flat();
    const currentFaq = faqs.filter((filteredFaq) => filteredFaq.id === Number(faqId));
    if (currentFaq.length === 0) {
      return {
        notFound: true,
      };
    }

    const isPaymentFaq = Number(faqId) === PAYMENT_FAQ;
    const isShippingFaq = Number(faqId) === SHIPPING_FAQ;
    const isSizesFaq = Number(faqId) === SIZES_FAQ;
    const isChangeSizeFaq = Number(faqId) === CHANGE_SIZE_FAQ;
    const isReturnOrderFaq = Number(faqId) === RETURN_ORDER_FAQ;

    const paymentMethodsPromise = isPaymentFaq ? getPaymentMethods({ context, lang: locale, countryId: getMarket(locale) }) : Promise.resolve({ paymentMethods: [] });
    const shippingMethodsPromise = isShippingFaq || isReturnOrderFaq || isChangeSizeFaq ? getShippingMethods({ context, lang: locale, countryId: getMarket(locale) }) : Promise.resolve({ shippingMethods: [] });
    const sizeGuidesPromise = isSizesFaq ? getFaqsSizeGuide({ context, profile: locale }) : Promise.resolve({ sizeGuides: [] });

    const [ { paymentMethods }, { shippingMethods }, { sizeGuides } ] = await Promise.all([ paymentMethodsPromise, shippingMethodsPromise, sizeGuidesPromise ]);

    const urlCanonical = `${baseUrl}/${locale}/at_cliente_faq?faq=${faqId}`;

    if (IS_CAMPER && currentFaq[0].answer) {
      const isMobile = getIsMobileSSR(context);
      const answerHasVideoResponsiveElements = currentFaq[0].answer.includes('class="video-mobile"') && currentFaq[0].answer.includes('class="video-desktop"');
      if (answerHasVideoResponsiveElements) {
        const regex = isMobile ? /<video[^>]*class=['"]video-desktop['"][^>]*>[\s\S]*?<\/video>/gi :
          /<video[^>]*class=['"]video-mobile['"][^>]*>[\s\S]*?<\/video>/gi;
        const answerCleaned = currentFaq[0].answer.replace(regex, '');
        currentFaq[0].answer = answerCleaned;
      }
    }
    return {
      props: {
        footerProps,
        tnmProps,
        ribbon,
        categories: faqsResponse.categories || [],
        currentFaq: currentFaq[0],
        paymentMethods: paymentMethods || [],
        shippingMethods: shippingMethods || [],
        sizeGuides: sizeGuides || [],
        urlCanonical,
      },
    };
  }

  const currentFaqs = faqsResponse.categories.filter((category) => category.id === Number(tit));
  if (currentFaqs.length === 0) {
    return {
      notFound: true,
    };
  }
  const urlCanonical = `${baseUrl}/${locale}/customer_service`;

  return {
    props: {
      footerProps,
      tnmProps,
      ribbon,
      categories: faqsResponse.categories || [],
      currentFaqs: currentFaqs[0].faqs,
      currentCategory: currentFaqs[0],
      isGroup: true,
      urlCanonical,
    },
  };
}

export default AtClienteFaq;
